/**
 * Shared utilities for Service Area bar graph widgets.
 */

export interface ServiceAreaInfo {
  id: number;
  name: string;
  code?: string;
}

export interface GroupConfig<T> {
  getServiceArea: (item: T) => ServiceAreaInfo | undefined;
  getTotal: (item: T) => number;
  getForemanAreaId: (item: T) => number;
}

export interface ServiceAreaGroupData<T> {
  serviceArea: ServiceAreaInfo;
  items: T[];
  total: number;
}

// Red-forward palette (keeps the WRAP identity) ORDERED for maximum contrast between
// adjacent stacked segments — the old list was six near-identical mid-reds that blurred
// together. Now consecutive indices alternate dark/bright/light so segments read apart;
// combined with the 2px white gaps in the bars. Slate tones tail in for bars with 7+
// segments so they don't wrap back to a red that's already used.
export const COLOR_PALETTE = [
  '#7F1D1D', // deep maroon
  '#EF4444', // bright red
  '#B91C1C', // dark red
  '#F87171', // light red
  '#DC2626', // medium red
  '#991B1B', // burgundy
  '#475569', // slate
  '#94A3B8', // light slate
  '#334155', // dark slate
  '#64748B', // mid slate
];

export function getDarkerColor(color: string, amount: number = 0.3): string {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const newR = Math.max(0, Math.round(r * (1 - amount)));
  const newG = Math.max(0, Math.round(g * (1 - amount)));
  const newB = Math.max(0, Math.round(b * (1 - amount)));
  return `rgb(${newR}, ${newG}, ${newB})`;
}

export function groupByServiceArea<T>(
  data: T[],
  config: GroupConfig<T>
): ServiceAreaGroupData<T>[] {
  const grouped = data.reduce((acc, item) => {
    const serviceArea = config.getServiceArea(item);
    if (!serviceArea) return acc;

    const serviceAreaId = serviceArea.id;
    if (!acc[serviceAreaId]) {
      acc[serviceAreaId] = {
        serviceArea,
        items: [],
        total: 0,
      };
    }

    acc[serviceAreaId].items.push(item);
    acc[serviceAreaId].total += config.getTotal(item);
    return acc;
  }, {} as Record<number, ServiceAreaGroupData<T>>);

  const result = Object.values(grouped);
  const sortKey = (g: ServiceAreaGroupData<T>) => {
    const code =
      g.serviceArea.code != null && g.serviceArea.code !== ''
        ? Number(g.serviceArea.code)
        : NaN;
    return Number.isFinite(code) ? code : g.serviceArea.id;
  };
  result.sort((a, b) => sortKey(a) - sortKey(b));

  result.forEach((group) => {
    group.items.sort((a, b) => config.getTotal(b) - config.getTotal(a));
  });

  return result;
}

export type ChartSize = 'small' | 'medium' | 'large';

export const CHART_SIZE_STYLES: Record<
  ChartSize,
  {
    barHeight: string;
    labelFontSize: string;
    gap: string;
    summaryFontSize: string;
    labelIconSize: string;
  }
> = {
  small: {
    barHeight: '20px',
    labelFontSize: '0.7rem',
    gap: '0.4rem',
    summaryFontSize: '0.7rem',
    labelIconSize: '10px',
  },
  medium: {
    barHeight: '32px',
    labelFontSize: '0.875rem',
    gap: '0.75rem',
    summaryFontSize: '0.875rem',
    labelIconSize: '12px',
  },
  large: {
    barHeight: '40px',
    labelFontSize: '0.875rem',
    gap: '1rem',
    summaryFontSize: '0.875rem',
    labelIconSize: '12px',
  },
};

/** Same as `ServiceAreaBarGraphWidget` inner chart `minHeight` (home page alignment). */
export const HOME_CHART_MIN_HEIGHT_PX = 200;

/**
 * Pie viewBox and geometry — sized so the home PCR pie matches the bar chart’s vertical footprint.
 */
export const HOME_PIE_CHART_PX = 200;
export const homePieCenter = () => {
  const c = HOME_PIE_CHART_PX / 2;
  return { cx: c, cy: c, r: 80 };
};
